"use client";

// T2 — full-price-dismiss fallback (PANDA-1500). We show the normal full-price
// Pro upgrade FIRST; only when the user dismisses it do we present the $1 offer.
// The 2-per-30-day cap + 7-day cooldown + person-level suppression are enforced
// SERVER-SIDE (the eligibility endpoint with surface=t2_dismiss) so users can't
// learn to dismiss the full-price modal to farm the discount.
//
// Zero-footprint integration: this mounts once on the project page and listens
// for the `panda:trial-fallback` CustomEvent the paywall's onClose dispatches
// (detail: { buildLabel? } — the personalised build name). Keeping the wiring to
// a single event + this component avoids touching the 15k-line project page.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrialOfferModal } from "@/components/trial/trial-offer-modal";
import { setModalOpen, MODAL_IDS } from "@/lib/ui/modal-coordinator";
import { redirectToBilling } from "@/lib/intentional-navigation";
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store";
import { isCustomCheckoutEnabled } from "@/lib/billing/checkout-mode";
import {
  trackTrialOfferShown,
  trackTrialOfferClicked,
  trackTrialOfferDismissed,
  trackTrialOfferSuppressed,
  trackTrialCheckoutFailed,
} from "@/lib/analytics";
import type { TrialOfferCopy, TrialProjectUsage } from "@/hooks/use-trial-offer";
import {
  coerceUpgradeCopyVariant,
  type UpgradeCopyVariant,
} from "@/lib/billing/upgrade-copy-experiment";
import {
  AUTO_DISPATCH_SURFACES,
  type TrialSurface,
  type TrialSurveySource,
} from "@/lib/trial/config";
import type { FirstBuildOfferTiming } from "@/lib/trial/first-build-offer";
import { TRIAL_ROUTING_TICKET } from "@/lib/trial/upgrade-entry-client";
import type {
  TrialEligibilityStatus,
  TrialFunnelProps,
  TrialTrigger,
  UpgradeEntryPoint,
  UpgradeSurface,
} from "@/lib/analytics";

interface FallbackDetail {
  buildLabel?: string | null;
  // When the dispatcher has ALREADY resolved eligibility (the credit-gate direct
  // path — PANDA-1500 / QA), it passes the offer here so this component shows it
  // as-is instead of running a second, independent eligibility check. That
  // avoids a split-brain where the caller skips the paywall on its own check but
  // this component's re-check then declines, leaving the user with no modal.
  offer?: TrialOfferCopy | null;
  // Which surface triggered this show, for exposure recording + funnel
  // attribution. Defaults to the classic paywall-dismiss surface; the in-build
  // credit gate passes "t5_credit_gate" so it is separable and skips the
  // t2_dismiss anti-farming cap (PANDA-1613 follow-up).
  surface?: TrialSurface;
  // Copy A/B arm + project usage from the dispatcher's eligibility read, for
  // the same reason `offer` is passed: this path must not re-check, so whatever
  // the server assigned has to travel with the event.
  copyVariant?: string | null;
  projects?: TrialProjectUsage | null;
  // Attribution from lib/trial/upgrade-entry-client (PANDA-1723). Absent on the
  // classic dismiss path, which is dispatched by the paywall itself and has no
  // upgrade entry point behind it — that case reports the paywall as its own
  // entry point below rather than leaving the funnel with a hole.
  entrypoint?: UpgradeEntryPoint | null;
  entrySurface?: UpgradeSurface | null;
  triggerReason?: string | null;
  eligibilityStatus?: TrialEligibilityStatus | null;
  projectId?: string | null;
  creditsRemaining?: number | null;
  // Which survey put this offer on screen (t8_survey only) and what the user
  // answered just before it. Carried so the exposure / click / dismissal rows
  // name the survey, not just the surface — four different surveys share
  // t8_survey and were otherwise one bucket.
  surveyId?: TrialSurveySource | null;
  surveyAnswer?: string | null;
  /** Finer-grained origin than `entrypoint` (e.g. "build_quality_negative"). */
  source?: string | null;
  // How long the post-first-build offer was held back before it fired
  // (lib/trial/first-build-offer.ts). Only the t7_first_build timer sets it;
  // it rides all three funnel events below so a dismissal can be read against
  // the wait that preceded it, not just the exposure.
  offerTiming?: FirstBuildOfferTiming | null;
}

const DEFAULT_SURFACE: TrialSurface = "t2_dismiss";

/** Key a dismissal by the project it happened on. Dispatches with no project
 *  (Settings, the dashboard) share one bucket of their own. */
const NO_PROJECT = "__no_project__";
function dismissalKey(projectId?: string | null): string {
  return projectId || NO_PROJECT;
}

/** Attribution for a dispatch that carried none — the classic paywall-dismiss
 *  and credit-gate paths, which predate the entry-point router. Named rather
 *  than left undefined so every trial_offer_* row has an `entrypoint` and the
 *  breakdown has no "(none)" bucket hiding two real surfaces. */
const UNROUTED_ATTRIBUTION: Record<
  string,
  { entrypoint: UpgradeEntryPoint; entrySurface: UpgradeSurface }
> = {
  t2_dismiss: {
    entrypoint: "rated_credits_modal",
    entrySurface: "workspace",
  },
  t5_credit_gate: {
    entrypoint: "credit_exhaustion_modal",
    entrySurface: "workspace",
  },
};


export function TrialDismissFallback() {
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState<TrialOfferCopy | null>(null);
  const [buildLabel, setBuildLabel] = useState<string | null>(null);
  const [surface, setSurface] = useState<TrialSurface>(DEFAULT_SURFACE);
  const [copyVariant, setCopyVariant] = useState<UpgradeCopyVariant>("control");
  const [projects, setProjects] = useState<TrialProjectUsage | null>(null);
  // Who opened this modal. Held as ONE object so the shown / clicked /
  // dismissed events for a single exposure can never be attributed to
  // different entry points if a second dispatch lands mid-life.
  const [attribution, setAttribution] = useState<{
    entrypoint: UpgradeEntryPoint | null;
    entrySurface: UpgradeSurface | null;
    triggerReason: string | null;
    eligibilityStatus: TrialEligibilityStatus | null;
    projectId: string | null;
    creditsRemaining: number | null;
    surveyId: TrialSurveySource | null;
    surveyAnswer: string | null;
    source: string | null;
    offerTiming: FirstBuildOfferTiming | null;
  }>({
    entrypoint: null,
    entrySurface: null,
    triggerReason: null,
    eligibilityStatus: null,
    surveyId: null,
    surveyAnswer: null,
    source: null,
    projectId: null,
    creditsRemaining: null,
    offerTiming: null,
  });

  // Whether this modal is currently up, and which projects the user has already
  // closed it on. Read inside the event handler, which is registered once, so
  // both are refs rather than state.
  const openRef = useRef(false);
  /**
   * The projects whose $1 offer the user has already dismissed.
   *
   * A SET, keyed by project, and that is the fix for the offer never appearing
   * on a second project. This host is mounted in the root layout and survives
   * every client-side navigation, so the single boolean it used to be turned
   * one dismissal on a first project into a permanent, session-wide veto: the
   * post-first-build timer on the user's SECOND project resolved eligible,
   * dispatched, and was dropped here without a sound. The rule it is actually
   * meant to enforce — "the timer must not re-show what the user just closed" —
   * is about one build, and a different project's first build is a different
   * moment with a different app to sell.
   *
   * A dispatch with no project (Settings, the dashboard) shares one key of its
   * own, so it can only ever suppress another project-less auto dispatch.
   */
  const dismissedProjectsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    async function onFallback(e: Event) {
      const detail = (e as CustomEvent<FallbackDetail>).detail ?? {};
      const requested = detail.surface ?? DEFAULT_SURFACE;

      // Dispatch now comes from ~9 independent places (the credit gate, seven
      // upgrade entry points, and a post-first-build timer) which cannot see
      // each other, so this is where duplicate opens are resolved.
      //
      // Already open → ignore. A second dispatch would rewrite `surface`
      // underneath the open modal, so the conversion and dismissal events would
      // be filed against a different surface than the exposure row written when
      // it opened.
      if (openRef.current) return;

      // Already dismissed → ignore, but ONLY for the surface the user did not
      // ask for, and ONLY on the project they dismissed it on. This guard exists
      // to stop the post-first-build TIMER from re-showing a modal the user just
      // closed; applying it to clicks made the Upgrade button dead on the second
      // press — the caller stands down on the promise that this listener opens
      // the modal (openUpgradeEntry returns handled:true the moment it
      // dispatches), so refusing here left the user with no modal at all. A
      // deliberate click is the user asking again, and must always be honoured;
      // the person-level exposure cap bounds it.
      //
      // Scoped per project because this host lives in the root layout and is
      // never remounted: unscoped, a single dismissal made the offer
      // first-project-only for the rest of the session.
      if (
        AUTO_DISPATCH_SURFACES.has(requested) &&
        dismissedProjectsRef.current.has(dismissalKey(detail.projectId))
      ) {
        // Never silently again. The router has already written a
        // trial_eligibility_evaluated row saying `trial_offer_shown: true` — it
        // reports the dispatch, not the modal — so without this the funnel
        // records an exposure that never happened and no reason for the gap.
        trackTrialOfferSuppressed(requested, {
          reason: "already_dismissed",
          entrypoint: detail.entrypoint ?? null,
          entry_surface: detail.entrySurface ?? null,
          trigger_reason: detail.triggerReason ?? null,
          eligibility_status: detail.eligibilityStatus ?? null,
          project_id: detail.projectId ?? null,
          credits_remaining: detail.creditsRemaining ?? null,
          source: detail.source ?? null,
          offer_timing: detail.offerTiming ?? null,
          source_ticket: TRIAL_ROUTING_TICKET,
        });
        return;
      }
      // Single eligibility decision. If the dispatcher already resolved the
      // offer (credit-gate direct path), use it verbatim — never re-check, so
      // the caller's decision to skip the paywall can't diverge from ours and
      // leave no modal on screen. Only the classic dismiss path (no offer in the
      // event) runs its own gate + T2 frequency-cap check here; a failure there
      // is safe because the full-price modal was already shown.
      let resolved = detail.offer ?? null;
      // Copy arm + project usage travel with the prefetched offer on the
      // credit-gate path; the classic dismiss path reads them from its own
      // eligibility response below. Either way they come from the server, never
      // from a client-side coin flip.
      let arm = coerceUpgradeCopyVariant(detail.copyVariant);
      let usage: TrialProjectUsage | null = detail.projects ?? null;
      if (!resolved) {
        try {
          const res = await fetch(
            "/api/billing/trial/eligibility?surface=t2_dismiss",
          );
          const data = await res.json();
          if (!data?.eligible || !data.offer) return;
          resolved = data.offer as TrialOfferCopy;
          arm = coerceUpgradeCopyVariant(data.copyVariant);
          const p = data.projects;
          usage =
            p && typeof p.current === "number" && typeof p.limit === "number"
              ? { current: p.current, limit: p.limit }
              : null;
        } catch {
          return; // fail closed
        }
      }
      setOffer(resolved);
      setCopyVariant(arm);
      setProjects(usage);
      setBuildLabel(detail.buildLabel ?? null);
      setSurface(requested);
      const fallbackAttribution = UNROUTED_ATTRIBUTION[requested];
      setAttribution({
        entrypoint: detail.entrypoint ?? fallbackAttribution?.entrypoint ?? null,
        entrySurface:
          detail.entrySurface ?? fallbackAttribution?.entrySurface ?? null,
        triggerReason: detail.triggerReason ?? null,
        // The classic dismiss path ran its own eligibility read just above and
        // returned early unless the server said yes, so reaching here at all
        // means eligible — record that rather than leaving it blank.
        eligibilityStatus: detail.eligibilityStatus ?? "eligible",
        projectId: detail.projectId ?? null,
        creditsRemaining: detail.creditsRemaining ?? null,
        surveyId: detail.surveyId ?? null,
        surveyAnswer: detail.surveyAnswer ?? null,
        source: detail.source ?? null,
        offerTiming: detail.offerTiming ?? null,
      });
      // The $1 offer is now shown INSTEAD of the paywall (decided upfront in
      // presentCreditLimit), so there is no full-price modal to animate out —
      // open immediately for a snappy, single-surface gate (PANDA-1500 / QA).
      setOpen(true);
    }
    window.addEventListener("panda:trial-fallback", onFallback);
    return () =>
      window.removeEventListener("panda:trial-fallback", onFallback);
  }, []);

  // Report this modal's open state to the global coordinator (PANDA-1500) so the
  // full-price paywall stands down while the $1 offer is up instead of stacking
  // back on top of it — the QA report. This modal owns its own `open` state
  // (it isn't a Radix dialog), so nothing else can see it without this.
  useEffect(() => {
    openRef.current = open;
    setModalOpen(MODAL_IDS.trialOffer, open);
    return () => setModalOpen(MODAL_IDS.trialOffer, false);
  }, [open]);

  // `surface` is always t2_dismiss or t5_credit_gate here — both valid
  // TrialTriggers — so the funnel + exposure are attributed to the real trigger.
  const trigger = surface as TrialTrigger;

  // One props object for all three funnel events, so an exposure, its click and
  // its dismissal always carry identical attribution.
  const funnelProps: TrialFunnelProps = useMemo(
    () => ({
      copy_variant: copyVariant,
      entrypoint: attribution.entrypoint,
      entry_surface: attribution.entrySurface,
      trigger_reason: attribution.triggerReason,
      eligibility_status: attribution.eligibilityStatus,
      project_id: attribution.projectId,
      credits_remaining: attribution.creditsRemaining,
      survey_id: attribution.surveyId,
      survey_answer: attribution.surveyAnswer,
      source: attribution.source,
      offer_timing: attribution.offerTiming,
      source_ticket: TRIAL_ROUTING_TICKET,
    }),
    [copyVariant, attribution],
  );

  const recordShown = useCallback(() => {
    trackTrialOfferShown(trigger, funnelProps);
    void fetch("/api/billing/trial/expose", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ surface }),
    }).catch(() => {});
  }, [surface, trigger, funnelProps]);

  const handleStart = useCallback(async () => {
    trackTrialOfferClicked(trigger, funnelProps);
    // Payments-paused guard. The $1 trial is a real card charge, so it fails
    // like any other. After trackTrialOfferClicked so the trial funnel still
    // records the click it would have recorded.
    if (
      interceptIfPaymentsPaused({
        kind: "trial",
        surface: `trial_offer_${surface}`,
        label: "Pro trial",
        priceLabel: "Free",
        plan: "pro",
        amountUsd: 0,
      })
    ) {
      return;
    }
    const returnTo = window.location.pathname;
    // The entry point rides all the way into Stripe metadata so the server-side
    // billing_trial_started can be joined back to the button that started it —
    // the ticket's "Stripe checkout and trial-start events can be joined back to
    // the original entrypoint". Without it the money events know only the coarse
    // surface, and every in-app button looks identical.
    const entrypoint = attribution.entrypoint ?? "";
    // Custom Payment Element checkout (behind the flag): go to our own /checkout
    // page instead of the hosted Checkout redirect.
    if (isCustomCheckoutEnabled()) {
      redirectToBilling(
        `/checkout?type=trial&surface=${encodeURIComponent(surface)}&entrypoint=${encodeURIComponent(entrypoint)}&returnTo=${encodeURIComponent(returnTo)}`,
      );
      return;
    }
    try {
      const res = await fetch("/api/billing/trial/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ surface, returnTo, entrypoint }),
      });
      const data = await res.json().catch(() => null);
      if (data?.url) {
        redirectToBilling(data.url as string);
        return;
      }
      // Clicked, but never reached Stripe. The modal still stays open to retry
      // — but silently, this was indistinguishable in the funnel from a user
      // who clicked and changed their mind.
      trackTrialCheckoutFailed(trigger, {
        ...funnelProps,
        reason: (data?.code as string) ?? (res.ok ? "no_url" : `http_${res.status}`),
        status: res.status,
      });
    } catch {
      // The request never completed. The server has no record of this at all,
      // so the client is the only place it can be counted.
      trackTrialCheckoutFailed(trigger, {
        ...funnelProps,
        reason: "network_error",
      });
      /* keep modal open to retry */
    }
  }, [surface, trigger, funnelProps, attribution.entrypoint]);

  // Soft exit ("Not now — stay on Free"). Same close + dismissal event as the X
  // so the funnel keeps one dismissal per exposure, tagged with the arm; the
  // separate handler exists so the decline can be told apart later without
  // changing the event contract now.
  const handleDecline = useCallback(() => {
    trackTrialOfferDismissed(trigger, funnelProps);
    dismissedProjectsRef.current.add(dismissalKey(attribution.projectId));
    setOpen(false);
  }, [trigger, funnelProps, attribution.projectId]);

  if (!offer) return null;

  // Personalisation ladder (design 2b): "Finish {their build} for $1", falling
  // back to the generic hook when the build context is thin.
  const headline = buildLabel ? `Finish ${buildLabel} free` : null;

  return (
    <TrialOfferModal
      open={open}
      onClose={() => {
        trackTrialOfferDismissed(trigger, funnelProps);
        dismissedProjectsRef.current.add(dismissalKey(attribution.projectId));
        setOpen(false);
      }}
      offer={offer}
      variant="dismiss"
      headline={headline}
      copyVariant={copyVariant}
      projects={projects}
      onStart={handleStart}
      onDecline={handleDecline}
      onShown={recordShown}
    />
  );
}
